import {
  IsBoolean,
  IsEmail,
  IsIn,
  IsOptional,
  IsString,
  MaxLength,
  ValidateIf,
} from 'class-validator';
import type { CitizenReportCategory } from './citizen-report.dto';

/**
 * Coarse origin of the matter. Superseded in detail by {@link IntakeMethod},
 * which the v47 spec makes mandatory at Stage 1 §A; this stays because the
 * notification and reporting paths already read it. It is derived from the
 * intake method's `source` flag rather than asked of the officer twice.
 */
export type ComplaintSource = 'citizen' | 'internal';

/**
 * How the matter reached the municipality (Stage 1 §A, mandatory).
 *
 * This is a routing decision, not a label: it selects which of the two
 * conditional intake sections the officer is asked to fill in. External
 * methods open §B (complainant / reporter information); internal methods open
 * §C (internal source detail). Nothing else decides that.
 *
 * A plain `string` because the list is administrable — see `taxonomy.dto.ts`.
 * The routing used to come from two membership arrays here; it now lives on
 * each row in `gov_intake_method`, so an administrator adding a method has to
 * say which sections it opens. Resolve it with `opensComplainantSection` and
 * `opensInternalSourceSection`.
 */
export type IntakeMethod = string;

/**
 * The complainant's relationship to the subject property (Stage 1 §B).
 *
 * Context only. The spec is explicit that it is "not a determinant of complaint
 * validity" — nothing downstream may gate on it.
 */
export type ComplainantRelationship =
  'adjacent_owner_occupant' | 'neighbour' | 'general_public' | 'business' | 'other';

export const COMPLAINANT_RELATIONSHIPS: ComplainantRelationship[] = [
  'adjacent_owner_occupant',
  'neighbour',
  'general_public',
  'business',
  'other',
];

export interface ComplaintDetail {
  id: string;
  source: ComplaintSource;
  /**
   * Stage 1 §B. When set, the complainant's identity and contact details are
   * retained internally for follow-up but must not appear in any outward-facing
   * output. The complaint report prints the complainant block only when this is
   * clear; every other generated document leaves them out entirely, anonymous
   * or not. See the anonymity guard in the gov-api forms specs.
   */
  anonymous: boolean;
  complainantPhone: string | null;
  /**
   * The complainant's own mailing address, in the four parts the complaint form
   * prints in separate boxes. Distinct from {@link subjectAddress}, which is the
   * property complained about. Null where the complainant did not give it.
   */
  complainantAddress: string | null;
  complainantCity: string | null;
  complainantProvince: string | null;
  complainantPostalCode: string | null;
  complainantRelationship: ComplainantRelationship | null;
  /** What the relationship was, when the code is `other`. Null otherwise. */
  complainantRelationshipOther: string | null;
  /**
   * Who took the complaint — "Plainte reçue par" on the form. Stamped from the
   * authenticated officer at intake, not typed, with the name snapshotted so the
   * record still reads after the account is renamed or removed.
   */
  receivedByUserId: string | null;
  receivedByName: string | null;
  /** Null on complaints captured before the field existed. */
  intakeMethod: IntakeMethod | null;
  /** How it actually arrived, when the method is `other`. Null otherwise. */
  intakeMethodOther: string | null;
  /** §C — who identified or referred the matter. Null when §C does not apply. */
  referringPerson: string | null;
  /** §C — one or two lines on how the matter came to light. */
  internalContext: string | null;
  complainantName: string;
  complainantEmail: string | null;
  /** Preferred language for complainant-facing notifications; null when unknown. */
  complainantLocale: 'en' | 'fr' | null;
  subjectAddress: string;
  description: string;
  createdAt: string;
  /**
   * What a citizen report added — the resident's category and pin — so
   * opening a file from it starts from them. Absent for every other complaint.
   */
  citizenReport?: {
    category: CitizenReportCategory | null;
    latitude: number | null;
    longitude: number | null;
  } | null;
}

export class CreateComplaintDto {
  @IsOptional()
  @IsIn(['citizen', 'internal'])
  source?: ComplaintSource;

  /**
   * Mandatory in the officer intake form, optional on the wire: the mobile
   * workflow and the dev seeds also create complaints and neither sends it
   * yet. Tighten to a bare `@IsIn` once every creation path is updated.
   */
  // Validated against `gov_intake_method` in the service — the accepted set is
  // administrable, so it cannot be a compile-time list.
  @IsOptional()
  @IsString()
  @MaxLength(32)
  intakeMethod?: IntakeMethod;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  intakeMethodOther?: string;

  @IsOptional()
  @IsString()
  referringPerson?: string;

  @IsOptional()
  @IsString()
  internalContext?: string;

  /** Defaults to false — a complaint is only anonymous if it says so. */
  @IsOptional()
  @IsBoolean()
  anonymous?: boolean;

  @IsOptional()
  @IsString()
  complainantPhone?: string;

  /**
   * The complainant's own mailing address, in the four parts the complaint form
   * prints in separate boxes — not the property, which is `subjectAddress`.
   * All optional: an officer takes what the caller volunteers.
   */
  @IsOptional()
  @IsString()
  @MaxLength(400)
  complainantAddress?: string;

  @IsOptional()
  @IsString()
  @MaxLength(120)
  complainantCity?: string;

  @IsOptional()
  @IsString()
  @MaxLength(64)
  complainantProvince?: string;

  @IsOptional()
  @IsString()
  @MaxLength(16)
  complainantPostalCode?: string;

  @IsOptional()
  @IsIn(COMPLAINANT_RELATIONSHIPS)
  complainantRelationship?: ComplainantRelationship;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  complainantRelationshipOther?: string;

  @IsString()
  complainantName!: string;

  @IsOptional()
  @IsEmail()
  complainantEmail?: string;

  @IsOptional()
  @IsIn(['en', 'fr'])
  complainantLocale?: 'en' | 'fr';

  @IsString()
  subjectAddress!: string;

  @IsString()
  description!: string;
}

/**
 * Correct an intake after the fact.
 *
 * Intake is typed once, often over the phone, and gets things wrong: a
 * misheard name, the wrong method, a description that reads badly a day later.
 * Nothing downstream is derived from these fields — they are the record of what
 * was reported — so they are correctable rather than frozen. Every field is
 * optional; the service applies the same §B/§C section gating as intake, so
 * changing the method to an internal one clears the complainant detail exactly
 * as it would have been dropped at creation.
 *
 * Not editable here: the property address and the tombstone, which the file
 * owns and the tombstone editor corrects.
 */
export class UpdateComplaintDto {
  // Validated against `gov_intake_method` in the service, as on create.
  @IsOptional()
  @IsString()
  @MaxLength(32)
  intakeMethod?: IntakeMethod;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  intakeMethodOther?: string;

  @IsOptional()
  @IsString()
  referringPerson?: string;

  @IsOptional()
  @IsString()
  internalContext?: string;

  @IsOptional()
  @IsBoolean()
  anonymous?: boolean;

  @IsOptional()
  @IsString()
  complainantName?: string;

  @IsOptional()
  @IsString()
  complainantPhone?: string;

  /**
   * The complainant's own mailing address, in the four parts the complaint form
   * prints in separate boxes — not the property, which is `subjectAddress`.
   * All optional: an officer takes what the caller volunteers.
   */
  @IsOptional()
  @IsString()
  @MaxLength(400)
  complainantAddress?: string;

  @IsOptional()
  @IsString()
  @MaxLength(120)
  complainantCity?: string;

  @IsOptional()
  @IsString()
  @MaxLength(64)
  complainantProvince?: string;

  @IsOptional()
  @IsString()
  @MaxLength(16)
  complainantPostalCode?: string;

  @IsOptional()
  @IsIn(COMPLAINANT_RELATIONSHIPS)
  complainantRelationship?: ComplainantRelationship;

  @IsOptional()
  @IsString()
  @MaxLength(200)
  complainantRelationshipOther?: string;

  // Blank is a legitimate value — an email captured in error has to be
  // removable — so the format check only applies to a non-empty string.
  @IsOptional()
  @IsString()
  @ValidateIf((o: { complainantEmail?: string }) => (o.complainantEmail ?? '').trim() !== '')
  @IsEmail()
  complainantEmail?: string;

  @IsOptional()
  @IsIn(['en', 'fr'])
  complainantLocale?: 'en' | 'fr';

  @IsOptional()
  @IsString()
  description?: string;
}
